import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import { postLoginPath } from "../../../utils/postLoginPath";

const BusinessPortalView = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'apply'
  const [loginEmail, setLoginEmail] = useState("");
  const [loginError, setLoginError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  const [applyForm, setApplyForm] = useState({
    name: "",
    businessName: "",
    website: "",
    email: "",
    phone: "",
  });
  const [applySubmitted, setApplySubmitted] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [applySubmitting, setApplySubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setResetMessage("");
    setLoginSubmitting(true);
    const password = e.target.password.value;
    try {
      const response = await api.login(loginEmail, password, false);
      onLoginSuccess(response.data.user);
      navigate(postLoginPath(response.data.user));
    } catch (err) {
      if (err.response?.status === 429) {
        setLoginError(t.tooManyLoginAttempts);
      } else {
        setLoginError(t.landingBizPortalLoginError);
      }
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoginError("");
    setResetMessage("");
    if (!loginEmail) {
      setLoginError(t.landingForgotNoEmail);
      return;
    }
    try {
      await api.requestPasswordReset(loginEmail);
      setResetMessage(t.landingForgotSent);
    } catch (err) {
      setLoginError(err.response?.data?.message || t.landingForgotError);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoginError("");
    try {
      const response = await api.googleLogin(credentialResponse.credential);
      onLoginSuccess(response.data.user);
      navigate(postLoginPath(response.data.user));
    } catch (err) {
      if (err.response?.status === 429) {
        setLoginError(t.tooManyLoginAttempts);
      } else {
        setLoginError(t.landingGoogleError);
      }
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplyError("");
    setApplySubmitting(true);
    try {
      await api.applyAsProspect(applyForm);
      setApplySubmitted(true);
    } catch (err) {
      setApplyError(t.landingApplyError);
    } finally {
      setApplySubmitting(false);
    }
  };

  const updateApplyField = (field) => (e) =>
    setApplyForm((prev) => ({ ...prev, [field]: e.target.value }));

  const sideItems = [
    {
      title: t.landingBizPortalSide1,
      subtitle: t.landingBizPortalSide1Sub,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-2-700)" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 12 2.5a7 7 0 0 1 7 7C19 14.8 12 21 12 21Z" />
          <circle cx="12" cy="9.5" r="2.25" />
        </svg>
      ),
    },
    {
      title: t.landingBizPortalSide2,
      subtitle: t.landingBizPortalSide2Sub,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-2-700)" strokeWidth="2.75" strokeLinecap="round">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      ),
    },
    {
      title: t.landingBizPortalSide3,
      subtitle: t.landingBizPortalSide3Sub,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-2-700)" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      ),
    },
  ];

  return (
    <main
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        minHeight: "calc(100vh - 64px)",
      }}
      className="sp-portal-split"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 40,
          borderRight: "1px solid var(--color-divider)",
        }}
      >
        {mode === "login" && (
          <div style={{ width: "100%", maxWidth: 380 }}>
            <div className="tag tag-accent">{t.landingBizPortalKicker}</div>
            <h1 style={{ fontSize: 34, marginTop: 12 }}>{t.landingBizPortalHeading}</h1>
            <p style={{ opacity: 0.8 }}>{t.landingBizPortalSubtitle}</p>
            <form
              style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 20 }}
              onSubmit={handleLogin}
            >
              <div className="field">
                <label>{t.landingBizPortalEmailLabel}</label>
                <input
                  className="input"
                  name="email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t.landingBizPortalPasswordLabel}</label>
                <input className="input" name="password" type="password" required />
              </div>
              <a
                href="#forgot"
                className="btn-ghost"
                style={{ alignSelf: "flex-end", fontSize: 13, textDecoration: "none" }}
                onClick={handleForgotPassword}
              >
                {t.landingBizPortalForgot}
              </a>
              <button type="submit" className="btn btn-primary btn-block" style={{ padding: 12 }} disabled={loginSubmitting}>
                {t.landingBizPortalLoginBtn}
              </button>
            </form>
            {loginError && (
              <p style={{ marginTop: 12, fontSize: 13, color: "var(--color-accent-700)" }}>{loginError}</p>
            )}
            {resetMessage && (
              <p style={{ marginTop: 12, fontSize: 13, color: "var(--color-accent-2-700)" }}>{resetMessage}</p>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
              <div className="hr" style={{ flex: 1, margin: 0 }} />
              <span style={{ fontSize: 12, opacity: 0.6 }}>{t.landingOrDivider}</span>
              <div className="hr" style={{ flex: 1, margin: 0 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setLoginError(t.landingGoogleError)}
                width={300}
              />
            </div>
            <p style={{ marginTop: 20, fontSize: 13, textAlign: "center" }}>
              {t.landingBizPortalNoAccount}{" "}
              <a href="#apply" onClick={(e) => { e.preventDefault(); setMode("apply"); }}>
                {t.landingBizPortalApply}
              </a>
            </p>
          </div>
        )}

        {mode === "apply" && (
          <div style={{ width: "100%", maxWidth: 420 }}>
            <div className="tag tag-accent">{t.landingApplyKicker}</div>
            <h1 style={{ fontSize: 34, marginTop: 12 }}>{t.landingApplyHeading}</h1>
            <p style={{ opacity: 0.8 }}>{t.landingApplySubtitle}</p>

            {applySubmitted ? (
              <div className="card elev-sm" style={{ marginTop: 20, padding: 20, background: "var(--color-accent-100)" }}>
                <p style={{ margin: 0, fontSize: 14 }}>{t.landingApplySuccess}</p>
              </div>
            ) : (
              <form
                style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 20 }}
                onSubmit={handleApply}
              >
                <div className="field">
                  <label>{t.landingApplyNameLabel}</label>
                  <input className="input" type="text" required value={applyForm.name} onChange={updateApplyField("name")} />
                </div>
                <div className="field">
                  <label>{t.landingApplyBusinessLabel}</label>
                  <input className="input" type="text" required value={applyForm.businessName} onChange={updateApplyField("businessName")} />
                </div>
                <div className="field">
                  <label>{t.landingApplyWebsiteLabel}</label>
                  <input className="input" type="text" value={applyForm.website} onChange={updateApplyField("website")} />
                </div>
                <div className="field">
                  <label>{t.landingApplyEmailLabel}</label>
                  <input className="input" type="email" required value={applyForm.email} onChange={updateApplyField("email")} />
                </div>
                <div className="field">
                  <label>{t.landingApplyPhoneLabel}</label>
                  <input className="input" type="tel" required value={applyForm.phone} onChange={updateApplyField("phone")} />
                </div>
                {applyError && (
                  <p style={{ fontSize: 13, color: "var(--color-accent-700)" }}>{applyError}</p>
                )}
                <button type="submit" className="btn btn-primary btn-block" style={{ padding: 12 }} disabled={applySubmitting}>
                  {t.landingApplySubmitBtn}
                </button>
              </form>
            )}
            <p style={{ marginTop: 20, fontSize: 13, textAlign: "center" }}>
              <a
                href="#login"
                onClick={(e) => {
                  e.preventDefault();
                  setMode("login");
                  setApplySubmitted(false);
                }}
              >
                {t.landingApplyBackToLogin}
              </a>
            </p>
          </div>
        )}
      </div>
      <div style={{ background: "var(--color-bg)", display: "flex", flexDirection: "column", justifyContent: "center", padding: 56 }}>
        <div className="tag tag-accent-2" style={{ alignSelf: "flex-start" }}>{t.landingBizPortalSideKicker}</div>
        <h2 style={{ fontSize: 30, marginTop: 12 }}>{t.landingBizPortalSideHeading}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
          {sideItems.map((item) => (
            <div key={item.title} className="card elev-sm" style={{ padding: 16 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: "var(--color-accent-2-100)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 10,
                }}
              >
                {item.icon}
              </div>
              <div style={{ fontFamily: "var(--font-heading)", fontSize: 18 }}>{item.title}</div>
              <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>{item.subtitle}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default BusinessPortalView;
